<template>
  <div class="press_release_filter">
    <div class="page-wrapper" v-if="category">
      <Grid
        :columns="['lg'].includes($mq) ? 2 : 1"
        class="press_release_filter__featured c_box--black c_text--white"
        v-if="category.featuredPost && category.featuredPost.length"
      >
        <MediaImage
          v-if="category.featuredPost[0].featuredImage && category.featuredPost[0].featuredImage.length"
          class="press_release_filter__featured__image"
          background
          :alt="category.featuredPost[0].featuredImage[0].title"
          :sources="[
            { srcset: category.featuredPost[0].featuredImage[0].filterPageFeaturedImage.srcsetWebp, type: 'webp' },
            { srcset: category.featuredPost[0].featuredImage[0].filterPageFeaturedImage.srcset },
          ]"
        />
        <Flex
          direction="column"
          wrap="nowrap"
          justify="center"
          class="press_release_filter__featured__content c_wrapper"
        >
          <h2
            class="c_header--2 c_text--graphik_bold press_release_filter__featured__header"
            v-if="category.featuredPost[0].title"
          >
            {{ category.featuredPost[0].title }}
          </h2>
          <div
            class="c_text press_release_filter__featured__summary"
            v-if="category.featuredPost[0].articleSummary"
            v-html="category.featuredPost[0].articleSummary"
          ></div>
          <Button
            class="press_release_filter__featured__button"
            v-if="category.featuredPost[0].url"
            :href="category.featuredPost[0].url"
            :label-text="$t('Read Article')"
          />
        </Flex>
      </Grid>
      <FilterContent
        class="press_release_filter__filter_content"
        ref="content"
        :ignore-local-storage="true"
        search-field
        :category="category"
        :related-posts-ids="relatedPostsIds"
        :results-entries="resultsEntries"
        results-entry-type="pressRelease"
        :seomatic="seomatic"
        @onSortByChange="onSortByChange"
        v-if="category && resultsEntries"
      >
      </FilterContent>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  reactive,
  toRefs,
  useContext,
  useFetch,
  useMeta,
  useRoute,
  useRouter,
  useStore,
} from '@nuxtjs/composition-api';
import { log, selectedCategoriesFromCategoriesField } from 'JS/global.js';
import { gqlToObject } from 'JS/seomatic.js';
import { pageQuery, resultsQuery } from 'GQL/pages/filter_pages/pressReleaseFilterGql.js';

import Button from 'Components/button/Button.vue';
import Flex from 'Components/flex/Flex.vue';
import Grid from 'Components/grid/Grid.vue';
import FilterContent from 'Components/New_blogs/FilterContent.vue';
import MediaImage from 'Components/image/MediaImage.vue';

export default defineComponent({
  name: 'PageBlogFilter',
  head: {},
  components: {
    Button,
    Flex,
    Grid,
    MediaImage,
    FilterContent,
  },
  setup() {
    const { app, $craft, payload, $preview } = useContext();
    const route = useRoute();
    const router = useRouter();
    const store = useStore();
    const state = reactive({
      category: null,
      entriesOrderBy: 'postDate desc',
      relatedPostsIds: [],
      resultsEntries: null,
      seomatic: null,
    });

    useFetch(async () => {
      let data;
      if (!$preview && payload?.category) {
        data = payload;
      } else {
        const pageRequest = await $craft({
          apiLog: 'PageBlogFilter',
          query: pageQuery,
          variables: {
            site: store.state.global.localeSites[app.i18n.locale].siteHandle,
            uri: `${route.value.params.contentSection}/press-releases`,
          },
        });

        if (pageRequest?.data) {
          data = pageRequest.data;
        }
      }

      if (data?.category) {
        state.category = data.category;

        if (data.category.relatedProductCategories) {
          state.relatedPostsIds = selectedCategoriesFromCategoriesField(data.category.relatedProductCategories);
        }

        if (data.category?.seomatic) {
          state.seomatic = data.category.seomatic;
        }

        // todo Fold this into payload setup
        const resultsRequest = await $craft({
          apiLog: 'PageBlogFilter, second request',
          query: resultsQuery,
          variables: {
            site: store.state.global.localeSites[app.i18n.locale].siteHandle,
            id: parseInt(state.category.id),
          },
        });
        if (resultsRequest?.data?.entries) {
          // Format product filters
          resultsRequest.data.entries.forEach((item) => {
            const formattedFilters = [];
            item.pressReleaseFilters.forEach((filter) => {
              formattedFilters.push(`${filter.parent.slug}:${filter.slug}`);
            });
            item.formattedFilters = formattedFilters;
            // this.$set(item, 'formattedFilters', formattedFilters);
          });

          state.resultsEntries = resultsRequest.data.entries;
        }
      } else {
        router.replace('/error/404');
      }
      store.commit('global/updateGlobalDarkTheme', false);
    });

    useMeta(() => {
      return state.seomatic ? { ...gqlToObject(state.seomatic) } : {};
    });

    return { ...toRefs(state) };
  },
  methods: {
    onSortByChange(newValue) {
      this.entriesOrderBy = newValue;
      this.updateBlogEntries();
    },
  },
  mounted() {
    log('PageProductFilter loaded');
  },
});
</script>

<style lang="scss">
.press_release_filter {
  $self: &;
  @at-root #{$self}__featured {
    @at-root #{&}__image {
      min-height: px_to_rem(300);
      @include mq(400) {
        min-height: px_to_rem(400);
      }
    }
    @at-root #{&}__content {
      padding-top: px_to_rem(40);
      padding-bottom: px_to_rem(40);
      box-sizing: border-box;
      width: 100%;
      @include mq($mq_sm_to_md) {
        padding-top: 4vw;
        padding-bottom: 4vw;
      }
      @include mq($mq_md_to_lg) {
        min-height: calc(#{px_to_rem(400)} + 8vw);
      }
    }
    @at-root #{&}__summary {
      padding-top: px_to_rem(20);
    }
    @at-root #{&}__button {
      margin-top: px_to_rem(30);
    }
  }
  @at-root #{$self}__intro {
    background: $color_gray_235;
    padding-top: px_to_rem(40);
    padding-bottom: px_to_rem(40);
    @at-root #{&}__header {
      padding-bottom: px_to_rem(20);
    }
  }
}
</style>
